import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Eye, Download, ZoomIn, ZoomOut, Maximize, Move, Palette, FileImage, ShieldAlert } from "lucide-react";

export function SvgViewer() {
  const [svgCode, setSvgCode] = useState(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
  <defs>
    <linearGradient id="rainbow" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#ec4899" />
      <stop offset="50%" stop-color="#8b5cf6" />
      <stop offset="100%" stop-color="#3b82f6" />
    </linearGradient>
  </defs>
  <rect width="100" height="100" rx="20" fill="url(#rainbow)" />
  <path d="M30 50 L45 65 L70 35" stroke="#ffffff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none" />
</svg>`);

  const [scale, setScale] = useState(1.0);
  const [backgroundType, setBackgroundType] = useState<"checkerboard" | "light" | "dark" | "emerald" | "amber">("checkerboard");
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 4.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.2));
  const handleResetZoom = () => setScale(1.0);

  // Rasterize SVG code and export as PNG image using Canvas
  const exportAsPng = () => {
    setError(null);
    try {
      const svgString = svgCode.trim();
      if (!svgString) return;

      // Extract width/height or use fallback
      const widthMatch = svgString.match(/width=["'](\d+)(\w*)["']/i);
      const heightMatch = svgString.match(/height=["'](\d+)(\w*)["']/i);
      
      let width = widthMatch ? parseInt(widthMatch[1]) : 500;
      let height = heightMatch ? parseInt(heightMatch[1]) : 500;

      // Ensure proper dimensions
      if (width <= 0) width = 500;
      if (height <= 0) height = 500;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Unable to create 2D canvas context.");
      }

      const img = new Image();
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = "vector_export.png";
        downloadLink.click();
        URL.revokeObjectURL(url);
      };

      img.onerror = () => {
        setError("Unable to render SVG into raster canvas. Check syntax tags.");
        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch (err: any) {
      setError(err?.message || "An unexpected error occurred during PNG export.");
    }
  };

  const parseSvgElements = () => {
    const tags = ["path", "rect", "circle", "ellipse", "line", "polygon", "polyline", "text", "g"];
    const found: { [key: string]: number } = {};
    tags.forEach(tag => {
      const count = (svgCode.match(new RegExp(`<${tag}\\b`, "g")) || []).length;
      if (count > 0) found[tag] = count;
    });
    return found;
  };

  const parsedStats = parseSvgElements();

  return (
    <ToolPageLayout
      title="SVG Viewer Online - Free Interactive Vector Graphics Viewer"
      description="View, inspect, zoom, and test SVG files online. Preview layouts over checkerboard, dark, light and custom backdrops, inspect XML structure, and export vector graphics directly to PNG formats."
      h1="SVG Viewer"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Paste your raw SVG code or inline graphic text in the XML input editor.",
        "Toggle backgrounds (grid checkerboards, lights, deep darks, or custom palettes) to verify color contrasts.",
        "Utilize scaling controls (Zoom In, Zoom Out, Reset) to check responsiveness and vector details.",
        "Export the current SVG code into a portable, high-resolution PNG file instantly."
      ]}
      faq={[
        { question: "What is an online SVG viewer used for?", answer: "An online SVG viewer renders XML vector graphics instantly. It allows frontend designers, developers, and writers to inspect vector details, check path coordinates, verify dark mode rendering, and check viewport properties safely without installing software." },
        { question: "Does this interactive SVG viewer allow export to PNG formats?", answer: "Yes. Our tool utilizes high-fidelity canvas rasterization vectors to draw and export the loaded SVG structure as a standard PNG image with a single click." },
        { question: "Can I inspect the number of elements (such as circles, paths, rects) inside my SVG?", answer: "Yes, our SVG inspector sidebar automatically parses the loaded SVG code string to report counts of paths, groups, texts, shapes, and other nested elements." },
        { question: "Why does my SVG look completely blank inside the viewer?", answer: "If your SVG appears blank, it is likely that the stroke or fill colors are set to white while viewing on a white background, or the SVG is missing structural 'viewBox', 'width', or 'height' attributes." },
        { question: "Is my private design graphic secure when using this online viewer?", answer: "Yes. All code parsing, zooming, element scanning, and raster downloads execute completely inside your web browser sandbox. No SVG strings or files are uploaded to any server." },
        { question: "How do I fix SVGs that do not scale correctly in responsive layouts?", answer: "Ensure your `<svg>` tag includes a valid `viewBox` attribute (e.g., viewBox='0 0 100 100') instead of only fixed pixel widths and heights, which lock dimensions on dynamic layouts." },
        { question: "What is the benefit of previewing SVGs on a checkerboard background?", answer: "A checkerboard pattern displays transparent grids. This allows you to immediately verify which parts of your graphic are fully transparent, semi-transparent, or solid opaque white." },
        { question: "Can I view SVGs that contain embedded images or external web fonts?", answer: "Yes, the browser will attempt to resolve external font files (like Google Fonts) and base64-encoded image sources, rendering them accurately in the viewport stage." },
        { question: "Does this tool support dragging or panning large SVGs?", answer: "The viewer supports fluid interactive zooming (up to 400%) so you can examine microscopic details, complex map coordinate points, and high-precision curves." },
        { question: "Is SVG format better than SVGZ for viewing on websites?", answer: "SVG format is readable text, making it perfect for direct debugging and viewing. SVGZ is compressed binary data used on live production web servers to minimize page payload sizes." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Input & Inspector Column */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">SVG Markup Input</Label>
              <Textarea
                value={svgCode}
                onChange={(e) => setSvgCode(e.target.value)}
                className="font-mono text-[10px] leading-relaxed min-h-[300px] bg-slate-50"
                placeholder="Paste your <svg> tags and markup here..."
              />
            </div>

            {/* Elements Inspector Panel */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Node Inspector</span>
              {Object.keys(parsedStats).length > 0 ? (
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                  {Object.entries(parsedStats).map(([tag, count]) => (
                    <div key={tag} className="flex justify-between border-b border-slate-50 pb-1">
                      <span className="font-mono text-slate-500">&lt;{tag}&gt;</span>
                      <strong className="text-slate-800">{count}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">No vector nodes found in markup.</p>
              )}
            </div>
          </div>

          {/* Interactive Viewer Stage */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-3">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                {/* Background selector */}
                <div className="flex items-center gap-1.5">
                  <Palette className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-600 mr-1.5">Background:</span>
                  <div className="flex gap-1">
                    {(["checkerboard", "light", "dark", "emerald", "amber"] as const).map(bg => (
                      <button
                        key={bg}
                        onClick={() => setBackgroundType(bg)}
                        title={`Select ${bg} background`}
                        className={`w-5 h-5 rounded-full border transition-all ${
                          backgroundType === bg ? "ring-2 ring-rose-500 scale-110" : ""
                        } ${
                          bg === "checkerboard" ? "checkerboard-bg-mini border-slate-300" :
                          bg === "light" ? "bg-white border-slate-200" :
                          bg === "dark" ? "bg-slate-900 border-slate-800" :
                          bg === "emerald" ? "bg-emerald-500 border-emerald-600" :
                          "bg-amber-400 border-amber-500"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Zoom Tools */}
                <div className="flex items-center gap-1">
                  <Button
                    onClick={handleZoomOut}
                    variant="outline"
                    size="icon"
                    className="w-8 h-8 text-slate-600 border-slate-200"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-xs font-mono font-bold text-slate-600 px-2 min-w-[50px] text-center">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    onClick={handleZoomIn}
                    variant="outline"
                    size="icon"
                    className="w-8 h-8 text-slate-600 border-slate-200"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleResetZoom}
                    variant="outline"
                    className="h-8 text-xs font-semibold border-slate-200 text-slate-600"
                  >
                    Reset
                  </Button>
                </div>
              </div>

              {/* Display Area */}
              <div
                ref={previewRef}
                className={`flex justify-center items-center min-h-[350px] rounded-xl border border-slate-100 overflow-auto relative transition-colors ${
                  backgroundType === "checkerboard" ? "checkerboard-bg" :
                  backgroundType === "light" ? "bg-slate-50" :
                  backgroundType === "dark" ? "bg-slate-950" :
                  backgroundType === "emerald" ? "bg-emerald-50" :
                  "bg-amber-50"
                }`}
              >
                <div
                  className="transition-transform duration-100 p-8 flex justify-center items-center"
                  style={{ transform: `scale(${scale})` }}
                  dangerouslySetInnerHTML={{ __html: svgCode }}
                />
              </div>

              {/* Status or errors */}
              {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-xs flex items-start gap-2.5">
                  <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-bold">Render Error:</strong> {error}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-1">
                <Button
                  onClick={exportAsPng}
                  disabled={!svgCode.trim()}
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white gap-2"
                >
                  <FileImage className="w-4 h-4" />
                  Export Visuals as PNG
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
